import React from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../services/firebase";
import { getCurrentUser } from "../../auth";
import { loadGradeSections, loadSectionAdvisers } from "../../services/sectionsService";

function campusVariants(campus) {
  const c = String(campus || "").trim();
  const lc = c.toLowerCase();
  if (!lc) return [];
  if (lc === "gapan") return ["gapan", "Gapan", "gapan city", "Gapan City"];
  if (lc === "bulacan") return ["bulacan", "Bulacan"];
  return Array.from(new Set([lc, c]));
}

function formatName(s) {
  const last = String(s.lastName || "").trim();
  const first = String(s.firstName || "").trim();
  const middle = String(s.middleName || s.middleInitial || "").trim();
  const core = `${last}${last && first ? ", " : ""}${first}`;
  return middle ? `${core} ${middle}` : core;
}

export default function Gradebook() {
  const user = getCurrentUser();
  const campus = String(user?.campus || "gapan").toLowerCase();
  const [grade, setGrade] = React.useState("");
  const [section, setSection] = React.useState("");
  const [subject, setSubject] = React.useState("");
  const [sectionsMap, setSectionsMap] = React.useState({});
  const [students, setStudents] = React.useState([]);

  React.useEffect(() => {
    (async () => {
      const map = await loadGradeSections(campus);
      // scope to teacher advisories if available
      try {
        const adv = await loadSectionAdvisers(campus);
        const tName = (user?.displayName || "").trim();
        const out = {};
        if (tName && adv) {
          Object.keys(adv || {}).forEach(g => {
            const secObj = adv[g] || {};
            const mine = Object.keys(secObj).filter(s => String(secObj[s] || "").trim() === tName);
            if (mine.length) out[g] = mine;
          });
        }
        setSectionsMap(Object.keys(out).length ? out : map || {});
      } catch { setSectionsMap(map || {}); }
    })();
  }, [campus, user?.displayName]);

  React.useEffect(() => {
    (async () => {
      if (!grade) { setStudents([]); return; }
      const base = collection(db, "students");
      const all = new Map();
      for (const c of campusVariants(campus)) {
        const q1 = query(base, where("campus","==",c), where("gradeLevel","==",grade));
        const snap1 = await getDocs(q1);
        snap1.docs.forEach(d => all.set(d.id, { id: d.id, ...d.data() }));
        if (section) {
          const q2 = query(base, where("campus","==",c), where("gradeLevel","==",grade), where("sections","array_contains",section));
          const snap2 = await getDocs(q2);
          snap2.docs.forEach(d => all.set(d.id, { id: d.id, ...d.data() }));
          const q3 = query(base, where("campus","==",c), where("gradeLevel","==",grade), where("section","==",section));
          const snap3 = await getDocs(q3);
          snap3.docs.forEach(d => all.set(d.id, { id: d.id, ...d.data() }));
          const q4 = query(base, where("campus","==",c), where("gradeLevel","==",grade), where("sections","==",section));
          const snap4 = await getDocs(q4);
          snap4.docs.forEach(d => all.set(d.id, { id: d.id, ...d.data() }));
        }
      }
      const rows = Array.from(all.values()).sort((a,b)=>formatName(a).localeCompare(formatName(b)));
      setStudents(rows);
    })();
  }, [grade, section, campus]);

  function exportCSV() {
    const headers = ["ID","Full Name","Grade","Section","Subject","Q1","Q2","Q3","Q4","Final"];
    const rows = students.map(s => [s.id, formatName(s), s.gradeLevel || "", (s.sections || [s.section || ""]).join(" / "), subject, "", "", "", "", ""]);
    const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${String(v||"").replace(/"/g,'""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `gradebook_${campus}_${grade}_${section || "all"}_${subject || "subject"}.csv`; a.click(); URL.revokeObjectURL(url);
  }

  const grades = Object.keys(sectionsMap || {});
  const sections = sectionsMap?.[grade] || [];

  return (
    <div className="container py-4">
      <h3 className="mb-3">Gradebook (Stub)</h3>
      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label">Grade</label>
            <select className="form-select" value={grade} onChange={e => { setGrade(e.target.value); setSection(""); }}>
              <option value="">Select</option>
              {grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Section</label>
            <select className="form-select" value={section} onChange={e => setSection(e.target.value)}>
              <option value="">All</option>
              {sections.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Subject</label>
            <input className="form-control" value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g., English" />
          </div>
          <div className="col-md-3 d-flex gap-2">
            <button className="btn btn-outline-primary" onClick={exportCSV} disabled={!grade}>Export CSV Template</button>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light"><tr><th>#</th><th>Name</th><th>Section</th></tr></thead>
            <tbody>
              {students.map((s, i) => (
                <tr key={s.id}><td>{i+1}</td><td>{formatName(s)}</td><td>{(s.sections || [s.section || ""]).join(" / ")}</td></tr>
              ))}
              {students.length === 0 && <tr><td colSpan="3" className="text-center text-muted">No students found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
